import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { Role } from '../../../../core/auth/role.enum';
import { CidaasUser } from '../../../../feature/user-administration/model/cidaas-user';
import { CompanyHierarchy } from '../../../../feature/user-administration/model/company-hierarchy';
import { CompanyInfo } from '../../../../feature/user-administration/model/company-info';
import { Rs2UsersQuery } from '../../../../feature/user-administration/model/rs2-users-query';
import { UserHierarchyResponse } from '../../../../feature/user-administration/model/user-hierarchy-response';
import { UserGroup } from '../../../../feature/user-management/user-group-management/model/user-group';
import { Constants } from '../../../helper/Constants';
import { SortOrder } from '../../../enum/sort-order.enum';
import { UserDirectoryCompany as Company } from '../../../model/user/user-directory-company';
import { UserDirectoryCompanyState } from '../../../model/user/user-directory-company-state.enum';
import { UserDirectoryUser } from '../../../model/user/user-directory-user';
import { IdentityService } from '../../../service/identity.service';
import { UserService } from '../../../service/user.service';
import { UserDirectoryCompanyTableComponent } from '../user-directory-company-table/user-directory-company-table.component';
import { UserDirectoryImportDialogComponent } from '../user-directory-import-dialog/user-directory-import-dialog.component';

interface CompanyHierarchyGroup {
  parent: Company;
  children: Company[];
}

@Component({
  selector: 'gate-user-directory-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    ButtonModule,
    PanelModule,
    UserDirectoryCompanyTableComponent,
    UserDirectoryImportDialogComponent,
  ],
  templateUrl: './user-directory-detail.component.html',
  styleUrl: '../user-directory.component.scss',
})
export class UserDirectoryDetailComponent implements OnInit {
  private identityService = inject(IdentityService);
  private userService = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  public selectedUser = signal<UserDirectoryUser | null>(null);
  public registeredCompanies = signal<Company[]>([]);
  public importableCompanies = signal<Company[]>([]);
  public isDetailLoading = signal<boolean>(false);
  public isImportDialogVisible = signal<boolean>(false);
  public isModuleAdmin = this.userService.currentUser().roles.includes(Role.ModuleAdmin);

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      const userKey = params.get('userKey');
      void this.loadSelectedUser(userKey ? decodeURIComponent(userKey) : '');
    });
  }

  public backToUsers(): void {
    this.router.navigate([Constants.routes.userAdministration()]);
  }

  public openImportDialog(): void {
    this.isImportDialogVisible.set(true);
  }

  public onImportDialogVisibleChange(visible: boolean): void {
    this.isImportDialogVisible.set(visible);
  }

  public async refreshCompanies(importedCompanies: Company[] = []): Promise<void> {
    const user = this.selectedUser();
    if (user) {
      await this.loadCompaniesForUser(user);
      this.moveCompaniesToRegistered(importedCompanies);
    }
  }

  public onCompaniesImported(companies: Company[]): void {
    if (companies.length === 0) {
      void this.refreshCompanies();
      return;
    }

    this.moveCompaniesToRegistered(companies);
    void this.refreshCompanies(companies);
  }

  public impersonate(userId: string | undefined, company: Company): void {
    if (!userId || !company.canImpersonate) {
      return;
    }

    company.isImpersonating = true;
    this.userService.startImpersonation({
      impersonatedUserSubject: userId,
      customerId: company.rs2CompanyNo,
      impersonatedCustomerName: company.name,
      impersonatedType: 'User',
      initiatorType: 'ACP_User',
      log: true,
    }).then(() => this.router.navigate(['home']))
      .catch(() => company.isImpersonating = false);
  }

  public getUserFullName(user: UserDirectoryUser | null): string {
    if (!user) {
      return '';
    }
    return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
  }

  private async loadSelectedUser(userKey: string): Promise<void> {
    this.isDetailLoading.set(true);
    this.registeredCompanies.set([]);
    this.importableCompanies.set([]);

    try {
      const stateUser = history.state?.user as UserDirectoryUser | undefined;
      const selected = stateUser?.key === userKey ? stateUser : await this.resolveUserFromRouteKey(userKey);
      this.selectedUser.set(selected);

      if (selected) {
        await this.loadCompaniesForUser(selected);
      }
    } catch (error) {
      console.error('Error loading selected user administration detail:', error);
      this.registeredCompanies.set([]);
      this.importableCompanies.set([]);
    } finally {
      this.isDetailLoading.set(false);
    }
  }

  private async resolveUserFromRouteKey(userKey: string): Promise<UserDirectoryUser | null> {
    const query = new Rs2UsersQuery();
    query.top = 10;
    query.skip = 0;
    query.searchString = userKey;
    query.sortExpression = {
      fieldName: 'cidaasCreationDate',
      order: SortOrder.Descending,
    };

    try {
      const result = await this.identityService.getCidaasUsers(query);
      const users = (result.cidaasUsers ?? []).map((item) => this.mapCidaasUser(item));
      return users.find((user) => user.key === userKey || user.email === userKey) ?? users[0] ?? null;
    } catch (error) {
      console.error('Error resolving selected user:', error);
      return null;
    }
  }

  private async loadCompaniesForUser(user: UserDirectoryUser): Promise<void> {
    const cidaasSub = user.payload.cidaas?.cidaasSub;
    if (!cidaasSub) {
      this.registeredCompanies.set([]);
      this.importableCompanies.set([]);
      return;
    }

    const hierarchy = await this.identityService.getUserHierarchy(cidaasSub);
    const couldBeAssignedGroups = this.mapCompanyHierarchy(
      this.getCouldBeAssignedCustomers(hierarchy),
      UserDirectoryCompanyState.CanBeRegistered,
    );
    const assignedGroups = this.mapCompanyHierarchy(
      this.getAssignedCustomers(hierarchy),
      UserDirectoryCompanyState.Registered,
      true,
    );
    const combinedCompanies = this.combineHierarchyGroups(couldBeAssignedGroups, assignedGroups);
    const canImpersonateMap = await this.loadCanImpersonateMap(combinedCompanies);
    const companies = combinedCompanies.map((company) => ({
      ...company,
      canImpersonate: company.canImpersonate || (canImpersonateMap[company.rs2CompanyNo] ?? false),
    }));

    this.registeredCompanies.set(companies.filter((company) => this.isRegisteredCompany(company)));
    this.importableCompanies.set(companies.filter((company) => !this.isRegisteredCompany(company)));
  }

  private async loadCanImpersonateMap(companies: Company[]): Promise<Record<string, boolean>> {
    const customerIds = companies
      .filter((company) => this.isRegisteredCompany(company))
      .map((company) => company.rs2CompanyNo)
      .filter((customerId): customerId is string => !!customerId);

    if (customerIds.length === 0) {
      return {};
    }

    try {
      return await firstValueFrom(this.identityService.checkCanImpersonate(customerIds));
    } catch (error) {
      console.error('Error checking impersonation permissions for user directory:', error);
      return {};
    }
  }

  private isRegisteredCompany(company: Company): boolean {
    if ((company.cidaasPermissions?.length ?? 0) > 0) {
      return true;
    }
    return company.registrationState === UserDirectoryCompanyState.Registered;
  }

  private moveCompaniesToRegistered(companies: Company[]): void {
    if (companies.length === 0) {
      return;
    }

    const importedKeys = new Set(
      companies.map((company) => this.getCompanyKey(company.tenant, company.rs2CompanyNo, company.name)),
    );
    const registeredKeys = new Set(
      this.registeredCompanies().map((company) => this.getCompanyKey(company.tenant, company.rs2CompanyNo, company.name)),
    );
    const importedRegisteredCompanies = companies
      .filter((company) => !registeredKeys.has(this.getCompanyKey(company.tenant, company.rs2CompanyNo, company.name)))
      .map((company) => ({
        ...company,
        registrationState: UserDirectoryCompanyState.Registered,
      }));

    if (importedRegisteredCompanies.length > 0) {
      this.registeredCompanies.set([...this.registeredCompanies(), ...importedRegisteredCompanies]);
    }
    this.importableCompanies.set(this.importableCompanies().filter(
      (company) => !importedKeys.has(this.getCompanyKey(company.tenant, company.rs2CompanyNo, company.name)),
    ));
  }

  private mapCidaasUser(item: CidaasUser): UserDirectoryUser {
    return {
      key: item.cidaasSub || item.userId || item.eMail,
      firstName: item.firstName,
      lastName: item.lastName,
      salutation: item.salutation,
      email: item.eMail,
      emailVerified: item.cidaasEMailVerified,
      createdAt: item.cidaasCreationDate ? new Date(item.cidaasCreationDate) : undefined,
      payload: { cidaas: item },
    };
  }

  private getAssignedCustomers(response: UserHierarchyResponse): CompanyHierarchy[] {
    return response.assignedCustomers ?? response.AssignedCustomers ?? [];
  }

  private getCouldBeAssignedCustomers(response: UserHierarchyResponse): CompanyHierarchy[] {
    return response.couldBeAssignedCustomers ?? response.CouldBeAssignedCustomers ?? [];
  }

  private getCompanyNo(item: CompanyInfo): string {
    return item.customerNo ?? item.customerId ?? '';
  }

  private getCompanyKey(tenant: string | undefined, companyNo: string, name: string | undefined): string {
    return `${tenant ?? ''}::${companyNo}::${name ?? ''}`;
  }

  private mapSingleCompanyInfo(item: CompanyInfo, state: UserDirectoryCompanyState): Company {
    const companyNo = this.getCompanyNo(item);
    const securityGroups = (item.securityGroups ?? [])
      .filter((group) => !!group.id && !!group.groupName)
      .map((group) => ({
        id: group.id!,
        groupName: group.groupName!,
        isDefault: false,
      } as UserGroup));
    const cidaasPermissions = (item.cidaasPermissions ?? item.CidaasPermissions ?? [])
      .filter((permission) => !!permission);
    const registrationState = cidaasPermissions.length > 0 ? UserDirectoryCompanyState.Registered : state;

    return {
      tenant: item.tenant,
      name: item.name,
      shortName: item.shortName,
      rs2CompanyNo: companyNo,
      address: {
        streetAddress: item.address,
        zipCode: item.zipCode,
        city: item.city,
        country: item.country,
      },
      createdAt: item.createdTime ? new Date(item.createdTime) : undefined,
      securityGroups,
      cidaasPermissions,
      registrationState,
      canImpersonate: companyNo ? this.userService.canImpersonate(companyNo, item.tenant) : false,
    };
  }

  private mapCompanyHierarchy(
    hierarchies: CompanyHierarchy[],
    state: UserDirectoryCompanyState,
    mergeDuplicateFirstChildPermissions = false,
  ): CompanyHierarchyGroup[] {
    const groups: CompanyHierarchyGroup[] = [];

    hierarchies.forEach((hierarchy) => {
      const parent = hierarchy.parent ?? hierarchy.Parent;
      const children = hierarchy.children ?? hierarchy.Children ?? [];

      if (!parent) {
        return;
      }

      const parentCompany = {
        ...this.mapSingleCompanyInfo(parent, state),
        hierarchyLevel: 0,
        isHierarchyChild: false,
      };
      const parentCompanyNo = this.getCompanyNo(parent);

      if (mergeDuplicateFirstChildPermissions && children.length > 0) {
        const firstChild = children[0];
        if (this.getCompanyNo(firstChild) === parentCompanyNo) {
          const duplicateChildCompany = this.mapSingleCompanyInfo(firstChild, state);
          parentCompany.cidaasPermissions = this.mergeUniqueStrings(
            parentCompany.cidaasPermissions,
            duplicateChildCompany.cidaasPermissions,
          );
          parentCompany.registrationState = this.resolveRegistrationState(parentCompany, duplicateChildCompany);
        }
      }

      const childRows: Company[] = children
        .filter((child) => this.getCompanyNo(child) !== parentCompanyNo)
        .map((child) => ({
          ...this.mapSingleCompanyInfo(child, state),
          hierarchyLevel: 1,
          isHierarchyChild: true,
        }));

      groups.push({
        parent: parentCompany,
        children: childRows,
      });
    });

    return groups;
  }

  private combineHierarchyGroups(
    couldBeAssignedGroups: CompanyHierarchyGroup[],
    assignedGroups: CompanyHierarchyGroup[],
  ): Company[] {
    const mergedByParent = new Map<string, CompanyHierarchyGroup>();
    const groupOrder: string[] = [];

    const upsertGroup = (group: CompanyHierarchyGroup): void => {
      const parentKey = this.getCompanyKey(group.parent.tenant, group.parent.rs2CompanyNo, group.parent.name);
      const existingGroup = mergedByParent.get(parentKey);

      if (!existingGroup) {
        mergedByParent.set(parentKey, {
          parent: { ...group.parent, hierarchyLevel: 0, isHierarchyChild: false },
          children: group.children.map((child) => ({
            ...child,
            hierarchyLevel: 1,
            isHierarchyChild: true,
          })),
        });
        groupOrder.push(parentKey);
        return;
      }

      existingGroup.parent = this.mergeCompanies(existingGroup.parent, group.parent, false);
      const childByKey = new Map<string, Company>();
      existingGroup.children.forEach((child) => {
        childByKey.set(this.getCompanyKey(child.tenant, child.rs2CompanyNo, child.name), child);
      });

      group.children.forEach((child) => {
        const childKey = this.getCompanyKey(child.tenant, child.rs2CompanyNo, child.name);
        const existingChild = childByKey.get(childKey);
        if (!existingChild) {
          childByKey.set(childKey, { ...child, hierarchyLevel: 1, isHierarchyChild: true });
          return;
        }
        childByKey.set(childKey, this.mergeCompanies(existingChild, child, true));
      });

      existingGroup.children = Array.from(childByKey.values());
    };

    couldBeAssignedGroups.forEach((group) => upsertGroup(group));
    assignedGroups.forEach((group) => upsertGroup(group));

    const flatCompanies: Company[] = [];
    groupOrder.forEach((key) => {
      const group = mergedByParent.get(key);
      if (!group) {
        return;
      }
      flatCompanies.push({
        ...group.parent,
        hierarchyLevel: 0,
        isHierarchyChild: false,
      });
      group.children.forEach((child) => {
        flatCompanies.push({
          ...child,
          hierarchyLevel: 1,
          isHierarchyChild: true,
        });
      });
    });

    return flatCompanies;
  }

  private mergeGroupsById(first: UserGroup[] | undefined, second: UserGroup[] | undefined): UserGroup[] {
    const mergedById = new Map<string, UserGroup>();
    [...(first ?? []), ...(second ?? [])].forEach((group) => {
      if (!group.id) {
        return;
      }
      if (!mergedById.has(group.id)) {
        mergedById.set(group.id, group);
      }
    });
    return Array.from(mergedById.values());
  }

  private resolveRegistrationState(first: Company, second: Company): UserDirectoryCompanyState {
    if (
      this.isRegisteredCompany(first)
      || this.isRegisteredCompany(second)
      || first.registrationState === UserDirectoryCompanyState.Registered
      || second.registrationState === UserDirectoryCompanyState.Registered
    ) {
      return UserDirectoryCompanyState.Registered;
    }
    return UserDirectoryCompanyState.CanBeRegistered;
  }

  private mergeCompanies(first: Company, second: Company, isChild: boolean): Company {
    const mergedCompanyNo = first.rs2CompanyNo || second.rs2CompanyNo;
    const mergedTenant = first.tenant || second.tenant;
    return {
      ...first,
      ...second,
      tenant: mergedTenant,
      name: first.name || second.name,
      shortName: first.shortName || second.shortName,
      rs2CompanyNo: mergedCompanyNo,
      address: {
        ...first.address,
        ...second.address,
      },
      createdAt: first.createdAt ?? second.createdAt,
      securityGroups: this.mergeGroupsById(first.securityGroups, second.securityGroups),
      cidaasPermissions: this.mergeUniqueStrings(first.cidaasPermissions, second.cidaasPermissions),
      registrationState: this.resolveRegistrationState(first, second),
      canImpersonate: first.canImpersonate || second.canImpersonate,
      hierarchyLevel: isChild ? 1 : 0,
      isHierarchyChild: isChild,
    };
  }

  private mergeUniqueStrings(...values: Array<string[] | undefined>): string[] {
    const merged = values.flatMap((value) => value ?? []).filter((value) => !!value);
    return Array.from(new Set(merged));
  }
}
